"use client"

/* 
  Drop-in resilient FormItem (React JSX)
  - Fixes crash when clicking Edit caused by reading `.size` on undefined form context.
  - Does not require external contexts; works standalone.
  - If you have useForm/useConfig and constants, you can rewire them back after adopting the guards.
*/

import React from "react"
import PropTypes from "prop-types"
import classNames from "classnames"
import { motion, AnimatePresence } from "framer-motion"

// Minimal fallbacks so this works without your shared constants
const CONTROL_SIZES = { sm: 8, md: 10, lg: 12 }
const LAYOUT = { HORIZONTAL: "horizontal", VERTICAL: "vertical", INLINE: "inline" }

const FormItem = React.forwardRef(function FormItem(
  {
    children,
    label,
    labelClass,
    errorMessage,
    invalid,
    className,
    layout,
    labelWidth,
    asterisk,
    style,
    size,
    extra,
    htmlFor,
  },
  ref,
) {
  // If you later wire contexts, use optional chaining:
  // const formContext = useForm?.() || {}
  // const { controlSize } = useConfig?.() || {}
  const formContext = {} // no provider required
  const controlSize = "md"

  const formItemLabelHeight = size || formContext?.size || controlSize || "md"
  const formItemLabelWidth = labelWidth ?? formContext?.labelWidth
  const formItemLayout = layout || formContext?.layout || LAYOUT.VERTICAL

  const getFormLabelLayoutClass = () => {
    const heightClass = CONTROL_SIZES?.[formItemLabelHeight] ? `h-${CONTROL_SIZES[formItemLabelHeight]}` : ""

    switch (formItemLayout) {
      case LAYOUT.HORIZONTAL:
        return label ? classNames(heightClass, "ltr:pr-2 rtl:pl-2") : "ltr:pr-2 rtl:pl-2"
      case LAYOUT.VERTICAL:
        return "mb-1"
      case LAYOUT.INLINE:
        return label ? classNames(heightClass, "ltr:pr-2 rtl:pl-2") : heightClass
      default:
        return ""
    }
  }

  const formItemClass = classNames("form-item", formItemLayout, className, {
    invalid: !!invalid,
  })

  const formLabelClass = classNames("form-label", label && getFormLabelLayoutClass(), labelClass)

  const formLabelStyle = () => {
    if (formItemLayout === LAYOUT.HORIZONTAL && formItemLabelWidth) {
      return { ...style, minWidth: formItemLabelWidth }
    }
    return { ...style }
  }

  const enterStyle = { opacity: 1, marginTop: 3, top: -21 }
  const exitStyle = { opacity: 0, marginTop: -10 }
  const initialStyle = exitStyle

  return (
    <div ref={ref} className={formItemClass}>
      {label ? (
        <div className="flex items-center justify-between">
          <label htmlFor={htmlFor} className={formLabelClass} style={formLabelStyle()}>
            <span className="inline-flex items-baseline gap-1">
              <span>{label}</span>
              {asterisk ? (
                <span aria-hidden="true" className="text-red-500">
                  *
                </span>
              ) : null}
            </span>
          </label>
          {extra ? <div className="text-xs text-gray-500">{extra}</div> : null}
        </div>
      ) : null}

      {/* Control/content */}
      <div className={formItemLayout === LAYOUT.HORIZONTAL ? "flex-1" : undefined}>{children}</div>

      {/* Error message with animation */}
      <AnimatePresence initial={false}>
        {invalid && errorMessage ? (
          <motion.div
            initial={initialStyle}
            animate={enterStyle}
            exit={exitStyle}
            role="alert"
            className="mt-1 text-xs text-red-600"
          >
            {errorMessage}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  )
})

FormItem.propTypes = {
  children: PropTypes.node,
  label: PropTypes.node,
  labelClass: PropTypes.string,
  errorMessage: PropTypes.node,
  invalid: PropTypes.bool,
  className: PropTypes.string,
  layout: PropTypes.oneOf([LAYOUT.HORIZONTAL, LAYOUT.VERTICAL, LAYOUT.INLINE]),
  labelWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  asterisk: PropTypes.bool,
  style: PropTypes.object,
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  extra: PropTypes.node,
  htmlFor: PropTypes.string,
}

FormItem.defaultProps = {
  invalid: false,
  asterisk: false,
  size: "md",
  layout: LAYOUT.VERTICAL,
}

export default FormItem
